import React from 'react';
import { useForm, ValidationError } from '@formspree/react';
import { TablesMap, SubHeading } from '../../components';
import { StyleSheet, css } from 'aphrodite';

const Bookings = () => {
  const [state, handleSubmit] = useForm('xyzyzkzr');

  if (state.succeeded) {
    return (
      <div className={css(styles.confirmationWrapper)}>
        <h3 className={css(styles.confirmationTitle)}>Reservation Confirmed!</h3>
        <p className={css(styles.confirmationText)}>
          Thank you for your reservation.<br />
          We've received your details and will contact you shortly to confirm your booking.
        </p>
      </div>
    );
  }

  return (
    <div className={css(styles.bookingsWrapper)}>
      <SubHeading title="Reserve a Table" />
      <h1 className={css(styles.pageTitle)}>Make a reservation</h1>
      <p className={css(styles.pageIntro)}>
        Choose your date, time and preferred table. Our team will confirm your booking shortly.
      </p>

      <form className={css(styles.formWrapper)} onSubmit={handleSubmit} method="POST">
        {/* Reservation Details */}
        <div className={css(styles.detailsBox)}>
          <div className={css(styles.formGroup)}>
            <label htmlFor="name" className={css(styles.label)}>Name</label>
            <input id="name" name="name" type="text" placeholder="Your Name here..." className={css(styles.input)} required />
            <ValidationError prefix="Name" field="name" errors={state.errors} />
          </div>

          <div className={css(styles.formGroup)}>
            <label htmlFor="email" className={css(styles.label)}>Email Address</label>
            <input id="email" name="email" type="email" placeholder="Your email here..." className={css(styles.input)} required />
            <ValidationError prefix="Email" field="email" errors={state.errors} />
          </div>

          <div className={css(styles.formGroup)}>
            <label htmlFor="phone" className={css(styles.label)}>Phone</label>
            <input id="phone" name="phone" type="tel" placeholder="Your phone number here..." className={css(styles.input)} required />
            <ValidationError prefix="Phone" field="phone" errors={state.errors} />
          </div>

          <div className={css(styles.formGroup)}>
            <label htmlFor="message" className={css(styles.label)}>Message (Optional)</label>
            <textarea id="message" name="message" rows={4} placeholder="Special request, number of guests etc..." className={css(styles.input)} />
            <ValidationError prefix="Message" field="message" errors={state.errors} />
          </div>

          <div className={css(styles.formGroup)}>
            <label htmlFor="date" className={css(styles.label)}>Select Date</label>
            <input id="date" type="date" name="date" className={css(styles.input)} required />
            <ValidationError prefix="Date" field="date" errors={state.errors} />
          </div>

          <div className={css(styles.formGroup)}>
            <label htmlFor="time" className={css(styles.label)}>Select Time</label>
            <input id="time" type="time" name="time" className={css(styles.input)} required />
            <ValidationError prefix="Time" field="time" errors={state.errors} />
          </div>
        </div>

        {/* Table Selection */}
        <div className={css(styles.tablesBox)}>
          <TablesMap />
          <button type="submit" className={css(styles.submitBtn)}>Reserve</button>
        </div>
      </form>
    </div>
  );
};

// --- Aphrodite Styles ---
const styles = StyleSheet.create({
  bookingsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '3rem',
    backgroundColor: 'var(--color-cream)',
    minHeight: '100vh',
    paddingTop: '10rem',

    '@media (max-width: 768px)': {
      marginTop: 0,
      padding: '8.5rem 1.25rem 3rem',
    },
  },

  pageTitle: {
    color: 'var(--color-green)',
    fontFamily: 'var(--font-base)',
    fontSize: 'clamp(2.6rem, 7vw, 5rem)',
    fontWeight: 500,
    lineHeight: 1.05,
    letterSpacing: '-.035em',
    textAlign: 'center',
    marginBottom: '1rem',
  },

  pageIntro: {
    color: 'var(--color-gray)',
    fontFamily: 'var(--font-alt)',
    fontSize: '.95rem',
    lineHeight: 1.7,
    maxWidth: '620px',
    textAlign: 'center',
    marginBottom: '2.5rem',
  },

  confirmationWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    minHeight: '80vh',
    backgroundColor: 'var(--color-cream)',
    color: 'var(--color-green)',
  },

  confirmationTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#c8b289',
    textAlign: 'center',
    marginBottom: '1rem',
  },

  confirmationText: {
    fontSize: '1.1rem',
    textAlign: 'center',
    color: '#ccc',
    lineHeight: 1.5,
  },

  formWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '2rem',
    width: '100%',
    maxWidth: '1200px',
  },

  detailsBox: {
    flex: '1',
    minWidth: '350px',
    padding: '2rem',
    backgroundColor: 'var(--color-paper)',
    borderRadius: '18px',
    border: '1px solid rgba(35,79,64,.1)',
    boxShadow: 'var(--shadow-soft)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.2rem',
  },

  tablesBox: {
    flex: '1',
    minWidth: '350px',
    padding: '2rem',
    backgroundColor: 'var(--color-green)',
    borderRadius: '18px',
    boxShadow: 'var(--shadow-soft)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },

  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },

  label: {
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
    fontWeight: '600',
    color: 'var(--color-green)',
    fontFamily: 'var(--font-alt)',
  },

  input: {
    padding: '0.6rem 0.8rem',
    borderRadius: '8px',
    border: '1px solid rgba(35,79,64,.2)',
    backgroundColor: 'var(--color-white)',
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-alt)',
    fontSize: '1rem',
    ':focus': {
      outline: 'none',
      borderColor: 'var(--color-green)',
      boxShadow: '0 0 0 4px rgba(35,79,64,.08)',
    },
  },

  submitBtn: {
    marginTop: '1rem',
    padding: '0.8rem 2rem',
    backgroundColor: 'var(--color-golden)',
    color: 'var(--color-green-deep)',
    fontWeight: '700',
    fontSize: '1rem',
    border: 'none',
    borderRadius: '999px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 28px rgba(0,0,0,.2)',
    },
  },
});

export default Bookings;
